<template>
  <a-input
    v-model:value="innerValue"
    type="email"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :maxlength="maxlength"
    :status="computedStatus"
    class="ant-email-input"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  >
    <template #prefix>
      <i class="fas fa-envelope" />
    </template>
  </a-input>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  maxlength?: number;
  status?: "" | "error" | "warning";
  validateOnBlur?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  placeholder: "Nhập email",
  disabled: false,
  size: "middle",
  allowClear: true,
  maxlength: 255,
  status: "",
  validateOnBlur: true,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
  (e: "change", value: string): void;
  (e: "validate", valid: boolean): void;
}>();

const isValidEmail = (email: string) => {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const localError = computed(() => {
  if (!props.validateOnBlur) return false;
  return !!props.modelValue && !isValidEmail(props.modelValue);
});

const computedStatus = computed(() => {
  if (props.status) return props.status;
  return localError.value ? "error" : "";
});

const innerValue = computed({
  get: () => props.modelValue ?? "",
  set: (val: string) => {
    emit("update:modelValue", val);
    emit("change", val);
  },
});

const onBlur = (e: FocusEvent) => {
  emit("validate", isValidEmail(props.modelValue ?? ""));
  emit("blur", e);
};

const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<!--
  Hướng dẫn sử dụng — Email
  Global: <h-email v-model="email" @validate="ok => {}" />
  Import: import { EmailInput } from '@/components/ant/input'

  Props: modelValue, placeholder, disabled, size, allowClear, maxlength, status, validateOnBlur
  Events: update:modelValue, change, blur, pressEnter, validate
  Docs: src/components/ant/input/README.md
-->
